import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ImagePlus,
  Loader2,
  MessageSquare,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Upload,
  ArrowLeftRight,
  Save,
  Presentation,
  X,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SectionCard, Segmented } from "@/components/common/Cards";
import { Sel } from "@/components/common/Sel";
import { TYPE_LABEL, fmtMeetingDate, imageUrl } from "@/components/meeting/meetUtils";

const STYLES = [
  { value: "presentasi", label: "Slide presentasi" },
  { value: "ilustrasi", label: "Ilustrasi" },
  { value: "foto", label: "Foto realistis" },
  { value: "diagram", label: "Diagram / infografis" },
];

export function SlidesPanel({ type, nextDate }) {
  const [slides, setSlides] = useState(null);
  const [idx, setIdx] = useState(0);
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("presentasi");
  const [target, setTarget] = useState("new"); // new | replace
  const [genBusy, setGenBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [form, setForm] = useState({ title: "", date: "", caption: "" });
  const [confirmDel, setConfirmDel] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(
    async (focusId) => {
      try {
        const { data } = await api.get("/meeting/slides", { params: { type } });
        setSlides(data);
        if (focusId) {
          const i = data.findIndex((s) => s.id === focusId);
          if (i >= 0) setIdx(i);
        } else setIdx((i) => Math.min(i, Math.max(0, data.length - 1)));
        return data;
      } catch (e) {
        toast.error(errMsg(e, "Gagal memuat slide"));
        setSlides([]);
        return [];
      }
    },
    [type]
  );

  useEffect(() => {
    setSlides(null);
    setIdx(0);
    load().then((d) => setIdx(Math.max(0, d.length - 1)));
  }, [type, load]);

  const cur = slides?.[idx] || null;
  useEffect(() => {
    setForm({ title: cur?.title || "", date: cur?.date || "", caption: cur?.caption || "" });
    if (!cur) setTarget("new");
  }, [cur?.id, cur?.title, cur?.date, cur?.caption]); // eslint-disable-line react-hooks/exhaustive-deps

  // keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (fullscreen && e.key === "Escape") { setFullscreen(false); return; }
      if (!slides?.length) return;
      if (e.key === "ArrowRight") setIdx((i) => Math.min(slides.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides, fullscreen]);

  const applyImage = async (img, sourceTitle) => {
    if (target === "replace" && cur) {
      await api.put(`/meeting/slides/${cur.id}`, { image_id: img.id });
      await load(cur.id);
      toast.success("Gambar slide diganti");
    } else {
      const { data } = await api.post("/meeting/slides", { type, image_id: img.id, title: sourceTitle || "", after_id: cur?.id || null });
      await load(data.id);
      toast.success("Slide baru dibuat");
    }
  };

  const generate = async () => {
    if (prompt.trim().length < 3) return toast.error("Tulis deskripsi gambar dulu.");
    setGenBusy(true);
    try {
      const { data } = await api.post("/meeting/images/generate", { prompt, style, meeting_type: type }, { timeout: 120000 });
      await applyImage(data, prompt.trim().slice(0, 80));
      setPrompt("");
    } catch (e) {
      toast.error(errMsg(e, "Gagal membuat gambar"));
    } finally {
      setGenBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pilih file gambar (JPG/PNG/WebP).");
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/meeting/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await applyImage(data, file.name.replace(/\.[^.]+$/, "").slice(0, 80));
    } catch (e) {
      toast.error(errMsg(e, "Gagal mengunggah"));
    } finally {
      setUpBusy(false);
    }
  };

  const blankSlide = async () => {
    try {
      const { data } = await api.post("/meeting/slides", { type, title: "Slide baru", after_id: cur?.id || null });
      await load(data.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveMeta = async () => {
    try {
      await api.put(`/meeting/slides/${cur.id}`, form);
      await load(cur.id);
      toast.success("Slide disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const move = async (dir) => {
    try {
      await api.post(`/meeting/slides/${cur.id}/move`, { direction: dir });
      await load(cur.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    setConfirmDel(false);
    try {
      await api.delete(`/meeting/slides/${cur.id}`);
      await load();
      toast.success("Slide dihapus");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/meeting/slides/${cur.id}/comments`, { text: comment });
      setComment("");
      await load(cur.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const delComment = async (cid) => {
    try {
      await api.delete(`/meeting/slides/${cur.id}/comments/${cid}`);
      await load(cur.id);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (slides === null) return <div className="h-[520px] rounded-xl border border-border bg-card animate-pulse" />;

  const dayLabel = type === "monday" ? "Senin" : "Kamis";

  return (
    <div className="grid xl:grid-cols-[1fr_360px] gap-5 items-start">
      <div className="space-y-4 min-w-0">
        {/* Date banner */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] px-4 py-3" data-testid="slide-date-banner">
          <CalendarDays className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[hsl(43_50%_60%)]">{TYPE_LABEL[type]}</div>
            <div className="font-display text-xl leading-tight" data-testid="slide-date">{fmtMeetingDate(cur?.date || nextDate)}</div>
          </div>
          {cur && cur.date !== nextDate && nextDate && (
            <Badge variant="outline" className="border-border text-[10px] text-muted-foreground">{dayLabel} berikutnya: {fmtMeetingDate(nextDate)}</Badge>
          )}
          <span className="ml-auto flex items-center gap-2" data-testid="slide-counter-wrap">
            <span className="text-sm num text-muted-foreground" data-testid="slide-counter">
              {slides.length ? `${idx + 1} / ${slides.length}` : "0 slide"}
            </span>
            {cur && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setFullscreen(true)}
                aria-label="Mode layar penuh"
                data-testid="slide-fullscreen-btn"
                title="Layar penuh"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
          </span>
        </div>

        {/* Slide frame */}
        <div className="relative rounded-xl border border-border bg-[hsl(220_14%_5%)] overflow-hidden elev-1 aspect-video" data-testid="slide-frame">
          {cur ? (
            <>
              {cur.image_id ? (
                <img src={imageUrl(cur.image_id)} alt={cur.title || "Slide"} className="absolute inset-0 h-full w-full object-contain bg-black" data-testid="slide-image" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                  <Presentation className="h-10 w-10 text-[hsl(43_45%_45%)]" />
                  <p className="text-sm text-muted-foreground mt-3">Tambahkan gambar dari panel kanan (pilih &quot;Ganti gambar slide ini&quot;).</p>
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8" data-testid="slides-empty">
              <Sparkles className="h-9 w-9 text-primary" />
              <div className="font-display text-3xl mt-3">Belum ada slide {TYPE_LABEL[type]}</div>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">Buat gambar dengan AI atau upload gambar di panel kanan untuk membuat slide pertama.</p>
              <Button variant="outline" className="mt-4" onClick={blankSlide} data-testid="slides-empty-blank"><Plus className="h-4 w-4 mr-2" /> Slide kosong</Button>
            </div>
          )}
          {slides.length > 1 && (
            <>
              <button type="button" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} aria-label="Slide sebelumnya" className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-[hsl(220_14%_4%/0.7)] border border-[color:var(--hairline)] flex items-center justify-center text-foreground hover:text-primary disabled:opacity-30 backdrop-blur-sm" data-testid="slide-prev">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => setIdx((i) => Math.min(slides.length - 1, i + 1))} disabled={idx === slides.length - 1} aria-label="Slide berikutnya" className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-[hsl(220_14%_4%/0.7)] border border-[color:var(--hairline)] flex items-center justify-center text-foreground hover:text-primary disabled:opacity-30 backdrop-blur-sm" data-testid="slide-next">
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {cur?.caption && <p className="text-sm text-[hsl(45_15%_82%)] leading-relaxed whitespace-pre-line" data-testid="slide-caption">{cur.caption}</p>}

        {/* Thumbnails */}
        {slides.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="slide-thumbs">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setIdx(i)}
                className={`relative shrink-0 w-32 aspect-video rounded-lg overflow-hidden border-2 ${i === idx ? "border-primary" : "border-border hover:border-[hsl(43_35%_40%)]"}`}
                data-testid={`slide-thumb-${i}`}
                aria-label={`Slide ${i + 1}`}
              >
                {s.image_id ? <img src={imageUrl(s.image_id)} alt="" className="h-full w-full object-cover" loading="lazy" /> : <div className="h-full w-full bg-[hsl(220_12%_9%)]" />}
                <span className="absolute left-1 bottom-1 text-[10px] num px-1.5 rounded bg-[hsl(220_14%_4%/0.8)]">{i + 1} · {s.date?.slice(5)}</span>
                {s.comments?.length > 0 && (
                  <span className="absolute right-1 top-1 text-[10px] px-1.5 rounded bg-primary text-primary-foreground flex items-center gap-0.5"><MessageSquare className="h-2.5 w-2.5" />{s.comments.length}</span>
                )}
              </button>
            ))}
            <button type="button" onClick={blankSlide} className="shrink-0 w-32 aspect-video rounded-lg border-2 border-dashed border-[hsl(220_10%_24%)] flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/60" data-testid="slide-add-blank" aria-label="Tambah slide kosong">
              <Plus className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Comments */}
        {cur && (
          <SectionCard title={`Komentar slide ${idx + 1}`} description="Catatan & masukan tim untuk slide ini." testid="slide-comments">
            <div className="space-y-3">
              {(cur.comments || []).length === 0 && <p className="text-sm text-muted-foreground" data-testid="slide-comments-empty">Belum ada komentar.</p>}
              {(cur.comments || []).map((c) => (
                <div key={c.id} className="flex gap-3 group" data-testid={`slide-comment-${c.id}`}>
                  <div className="h-8 w-8 rounded-full bg-[hsl(43_30%_12%)] border border-[color:var(--hairline)] flex items-center justify-center text-[11px] text-primary uppercase shrink-0">
                    {(c.author?.name || "?").slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs">
                      <span className="font-medium">{c.author?.name}</span> <span className="text-muted-foreground">· {fmtDate(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-[hsl(45_15%_84%)] whitespace-pre-line mt-0.5">{c.text}</p>
                  </div>
                  <button type="button" onClick={() => delComment(c.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(0_72%_66%)]" aria-label="Hapus komentar" data-testid={`slide-comment-delete-${c.id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendComment();
                    }
                  }}
                  rows={2}
                  placeholder="Tulis komentar… (Enter untuk kirim)"
                  className="bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm"
                  data-testid="slide-comment-input"
                />
                <Button onClick={sendComment} disabled={!comment.trim()} className="self-end" data-testid="slide-comment-send" aria-label="Kirim komentar">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SectionCard>
        )}
      </div>

      {/* Right panel */}
      <div className="space-y-4 xl:sticky xl:top-4">
        <SectionCard title="Gambar AI" eyebrow="Buat slide" testid="slide-ai-panel">
          <div className="space-y-3">
            <Segmented
              value={cur ? target : "new"}
              onChange={setTarget}
              size="sm"
              testid="slide-target"
              options={[
                { value: "new", label: "Slide baru" },
                ...(cur ? [{ value: "replace", label: "Ganti gambar slide ini" }] : []),
              ]}
            />
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} placeholder="mis. Target penjualan Q4 naik 20%, tim bergerak maju menuju garis finish" className="bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm" data-testid="slide-ai-prompt" />
            <Sel value={style} onChange={setStyle} options={STYLES} testid="slide-ai-style" />
            <Button onClick={generate} disabled={genBusy || upBusy} className="w-full h-11 press" data-testid="slide-ai-generate">
              {genBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {genBusy ? "Membuat gambar… (±20 dtk)" : "Generate gambar"}
            </Button>
            <div className="relative text-center text-[11px] text-muted-foreground"><span className="bg-card px-2 relative z-[1]">atau</span><span className="absolute left-0 right-0 top-1/2 h-px bg-border" /></div>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={genBusy || upBusy} className="w-full h-11" data-testid="slide-upload-btn">
              {upBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload gambar
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="slide-upload-input" />
          </div>
        </SectionCard>

        {cur && (
          <SectionCard title="Pengaturan slide" testid="slide-settings">
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Judul</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10 bg-[hsl(220_12%_8%)]" data-testid="slide-title-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Tanggal meeting ({dayLabel})</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-10 bg-[hsl(220_12%_8%)] num" data-testid="slide-date-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Catatan slide</Label>
                <Textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={3} className="mt-1 bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm" data-testid="slide-caption-input" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveMeta} className="press" data-testid="slide-save"><Save className="h-4 w-4 mr-1.5" /> Simpan</Button>
                <Button variant="outline" size="icon" onClick={() => move(-1)} disabled={idx === 0} aria-label="Geser ke kiri" data-testid="slide-move-left"><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" onClick={() => move(1)} disabled={idx === slides.length - 1} aria-label="Geser ke kanan" data-testid="slide-move-right"><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="ghost" onClick={() => setConfirmDel(true)} className="ml-auto text-muted-foreground hover:text-[hsl(0_72%_66%)]" data-testid="slide-delete"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><ArrowLeftRight className="h-3 w-3" /> Gunakan tombol ← → di keyboard untuk pindah slide. <ImagePlus className="h-3 w-3" /></p>
            </div>
          </SectionCard>
        )}
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus slide {idx + 1}?</AlertDialogTitle>
            <AlertDialogDescription>Slide beserta komentarnya akan dihapus.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="slide-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {fullscreen && cur && (
        <FullscreenSlide
          type={type}
          slide={cur}
          idx={idx}
          total={slides.length}
          onPrev={() => setIdx((i) => Math.max(0, i - 1))}
          onNext={() => setIdx((i) => Math.min(slides.length - 1, i + 1))}
          onClose={() => setFullscreen(false)}
        />
      )}
    </div>
  );
}

function FullscreenSlide({ type, slide, idx, total, onPrev, onNext, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[300] bg-black text-foreground flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Slide layar penuh"
      data-testid="slide-fullscreen"
    >
      {/* System header: brand + meeting type + slide date */}
      <header className="shrink-0 flex items-center gap-4 px-6 py-3 border-b border-[hsl(220_12%_12%)] bg-[hsl(220_14%_5%)]" data-testid="slide-fullscreen-header">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[3px] text-primary">
          <CalendarDays className="h-4 w-4" />
          {TYPE_LABEL[type]}
        </div>
        <div className="flex-1 min-w-0 text-center">
          <div className="font-display text-xl md:text-2xl truncate" data-testid="slide-fullscreen-date">
            {fmtMeetingDate(slide.date)}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs num text-muted-foreground" data-testid="slide-fullscreen-counter">
            {idx + 1} / {total}
          </span>
          <Button size="icon" variant="ghost" onClick={onClose} aria-label="Keluar layar penuh" data-testid="slide-fullscreen-close">
            <Minimize2 className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Slide content (image only, no title overlay) */}
      <div className="flex-1 relative overflow-hidden" data-testid="slide-fullscreen-stage">
        {slide.image_id ? (
          <img
            src={imageUrl(slide.image_id)}
            alt=""
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Presentation className="h-16 w-16 text-[hsl(43_45%_35%)]" />
          </div>
        )}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={onPrev}
              disabled={idx === 0}
              aria-label="Slide sebelumnya"
              className="absolute left-5 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[hsl(220_14%_4%/0.6)] border border-[color:var(--hairline)] flex items-center justify-center hover:text-primary disabled:opacity-20 backdrop-blur-sm"
              data-testid="slide-fullscreen-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={idx === total - 1}
              aria-label="Slide berikutnya"
              className="absolute right-5 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-[hsl(220_14%_4%/0.6)] border border-[color:var(--hairline)] flex items-center justify-center hover:text-primary disabled:opacity-20 backdrop-blur-sm"
              data-testid="slide-fullscreen-next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
